import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Calendar, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { formatCurrencyNoDecimals, formatPercentage } from '@/utils/formatters';
import { useFundHoldings } from '../hooks/useHoldings';
import { InformationAsOfLabel } from '@/portal/shared/components';

export function FundDetailPage() {
  const navigate = useNavigate();
  const { fundId } = useParams<{ fundId: string }>();

  const {
    data: fund,
    isLoading: fundLoading,
    error: fundError,
  } = useFundHoldings(fundId);

  if (fundLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (fundError || !fund) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/portal/holdings')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Holdings
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Fund Not Found
            </h3>
            <p className="text-gray-500 text-center max-w-md">
              The fund you&apos;re looking for could not be found or you
              don&apos;t have access to it.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currency = fund.base_currency;
  const uncalledCapital = fund.commitment_amount - fund.total_called;

  const getStatusBadge = (status: string | null) => {
    if (!status) return <Badge variant="secondary">Unknown</Badge>;
    const statusLower = status.toLowerCase();
    if (statusLower === 'active' || statusLower === 'investing') {
      return <Badge className="bg-green-100 text-green-800">Active</Badge>;
    }
    if (statusLower === 'closed' || statusLower === 'liquidated') {
      return <Badge variant="secondary">{status}</Badge>;
    }
    if (statusLower === 'harvesting') {
      return <Badge className="bg-amber-100 text-amber-800">Harvesting</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/portal/holdings')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">
                {fund.display_name || fund.name}
              </h1>
              {getStatusBadge(fund.status)}
            </div>
            <p className="text-gray-500 flex items-center gap-2 mt-1">
              <Calendar className="h-4 w-4" />
              Vintage {fund.vintage_year || 'N/A'} | {currency}
            </p>
          </div>
        </div>
        <InformationAsOfLabel date={fund.information_as_of} className="mt-1" />
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Commitment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {formatCurrencyNoDecimals(fund.commitment_amount, currency)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Called
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {formatCurrencyNoDecimals(fund.total_called, currency)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {formatPercentage(fund.call_percentage)} of commitment
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Prepayment Pool
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {formatCurrencyNoDecimals(fund.prepayment_pool, currency)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Available balance</p>
          </CardContent>
        </Card>
      </div>

      {/* Capital Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Capital Called Progress</CardTitle>
          <CardDescription>
            Track the capital call progress for your commitment
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Capital Called</span>
                <span className="font-medium">
                  {formatPercentage(fund.call_percentage)}
                </span>
              </div>
              <Progress value={fund.call_percentage * 100} className="h-4" />
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>
                  {formatCurrencyNoDecimals(fund.total_called, currency)} called
                </span>
                <span>
                  {formatCurrencyNoDecimals(uncalledCapital, currency)}{' '}
                  remaining
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
